import { Flex, Heading } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import Slider, { Settings } from "react-slick";

import ArtistCard from "@/components/ui/ArtistCard";
import { CreatorsControllerTopCreatorsTrackType } from "@/lib/lutely-api/gen/dtos";
import { useTopCreatorsInfiniteQuery } from "@/lib/lutely-api/hooks/useTopCreatorsInfiniteQuery";
import CarouselShell from "./CarouselShell";
import { useResponsiveSlidesToShow } from "./useResponsiveSlidesToShow";
import { useSliderSettings } from "./useSliderSettings";
import type { MarketplaceSearch } from "./types";

import "./slider-section.css";

type CreatorsSectionProps = {
  hideArrows?: boolean;
  kind: "SONG" | "BEAT" | "SOUNDKIT";
  searchParameters: MarketplaceSearch;
};

const trackTypeMap: Record<
  CreatorsSectionProps["kind"],
  CreatorsControllerTopCreatorsTrackType
> = {
  SONG: "SONG",
  BEAT: "BEAT",
  SOUNDKIT: "SOUNDKIT",
};

function parseList(value?: string): string[] | undefined {
  if (!value) return undefined;
  const parsed = value.split(", ").filter(Boolean);
  return parsed.length ? parsed : undefined;
}

export default function CreatorsSection({
  hideArrows,
  kind,
  searchParameters,
}: CreatorsSectionProps) {
  const genres = parseList(searchParameters.genre);
  const moods = parseList(searchParameters.mood);
  const trackType = trackTypeMap[kind];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useTopCreatorsInfiniteQuery(
      {
        pageSize: 14,
        genres,
        moods,
        trackType,
      },
      { enabled: true },
    );

  const creators = data?.pages.flatMap((page) => page.content) ?? [];
  const pageSize = data?.pages[0]?.content.length ?? 14;

  const sliderRef = useRef<Slider | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(7);
  const [carouselEl, setCarouselEl] = useState<HTMLDivElement | null>(null);

  const attachCarouselRef = useCallback((node: HTMLDivElement | null) => {
    setCarouselEl(node);
  }, []);

  const visibleSlides = useResponsiveSlidesToShow(carouselEl, 220); // 200px card + 20px gap
  const showLeftFade =
    !hideArrows && creators.length > visibleSlides && currentSlide > 0;
  const showRightFade =
    !hideArrows &&
    creators.length > visibleSlides &&
    currentSlide + visibleSlides < creators.length;

  useEffect(() => {
    if (
      currentSlide + itemsPerSlide >= creators.length - pageSize &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  }, [
    currentSlide,
    itemsPerSlide,
    creators.length,
    pageSize,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  useEffect(() => {
    sliderRef.current?.slickGoTo(0);
  }, [kind]);

  const settings: Settings = useSliderSettings({
    slidesToShow: visibleSlides,
    // Use floor so slidesToScroll is always an integer — fractional values get
    // rounded up by slick and skip creators that should be visible.
    slidesToScroll: Math.max(1, Math.floor(visibleSlides)),
    arrows: !hideArrows,
    beforeChange: (_, next) => setItemsPerSlide(Math.abs(next - currentSlide)),
    afterChange: setCurrentSlide,
    responsive: [],
  });

  if (!creators.length) return null;

  return (
    <Flex direction="column">
      <Flex className="mb-4 items-center">
        <Heading
          as="h2"
          className="font-figtree text-[24px] font-bold leading-[120%] text-[#050C2B]"
          style={{ fontFamily: "var(--Primary, Figtree)" }}
        >
          {kind === "SONG" ? "Featured Artists" : "Featured Producers"}
        </Heading>
      </Flex>

      <CarouselShell
        ref={attachCarouselRef}
        showLeftFade={showLeftFade}
        showRightFade={showRightFade}
        className="w-full marketplace-carousel--creators"
      >
        <Slider
          ref={sliderRef}
          {...settings}
          className={hideArrows ? "mobile-hide-arrows" : ""}
        >
          {creators.map((creator) => (
            <ArtistCard key={creator.creatorId} creator={creator} />
          ))}
        </Slider>
      </CarouselShell>
    </Flex>
  );
}
